/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useMemo } from 'react';
import { useLocation } from '@jira/navigation-apps__routing-hooks/src/useLocation.tsx';
import { useRoute } from '@jira/navigation-apps__routing-hooks/src/useRoute.tsx';
import { useConnectGeneralResource } from '@jira/router-resources__connect-general/src/useConnectGeneralResource.tsx';
import type { ConnectDataItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-connect/src/common/types.tsx';
import { findItemAndStack } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-connect/src/common/utils/find-item-stack/index.tsx';
import { getLinks } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-connect/src/common/utils/get-links/index.tsx';
import { transformLink } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-connect/src/common/utils/transform-link/index.tsx';
import { withThrowOnError } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/utils/throw-when-no-data-and-error/index.tsx';
import { useNavigationGlobalResource } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-common/src/controllers/navigation-global-resource/index.tsx';
import type { SidebarConfigData } from '@jira/navigation-apps__shared-types/src/types.tsx';
import type { RouteResourceError } from '@atlassian/react-resource-router';

const getItems = (d: SidebarConfigData) =>
	getLinks(d, ['admin_system_menu'], { skipFilter: true })
		.map(transformLink) // Apply transformation to each link
		.filter(Boolean); // Remove falsy values

export const useGlobalSystemSettingsConnectItems: () => {
	data: ConnectDataItem[] | null;
	loading: boolean;
	error: RouteResourceError | null;
} = withThrowOnError(() => {
	// eslint-disable-next-line jira/react/no-usage-of-hooks-outside-components
	const { data, loading, error } = useNavigationGlobalResource();

	const items: ConnectDataItem[] | null = useMemo(() => data && getItems(data), [data]);

	return {
		data: items,
		loading,
		error,
	};
});

export const useGlobalSystemSettingsConnectItemActive = (): {
	isActive: boolean;
	loading: boolean;
	error: RouteResourceError | null;
} => {
	const location = useLocation();
	const route = useRoute();
	const { data, loading, error } = useGlobalSystemSettingsConnectItems();
	const {
		data: connectGeneralData,
		loading: connectGeneralResourceLoading,
		error: connectGeneralResourceError,
	} = useConnectGeneralResource();

	const isActive = useMemo(
		() => Boolean(route && data && findItemAndStack(data, route.name, location)?.selected),
		[data, route, location],
	);

	return {
		isActive: isActive || connectGeneralData?.moduleType === 'adminPages',
		loading: loading || connectGeneralResourceLoading,
		error: error || connectGeneralResourceError,
	};
};
